import { Component, DOCUMENT, inject, OnInit } from '@angular/core';
import { FadeInReveal } from '../../../../directives/fade-in-reveal';
import { RouterLink } from '@angular/router';

interface FaqItem {
  question: string;
  answer: string;
  isOpen?: boolean;
}

interface FaqCategory {
  title: string;
  items: FaqItem[];
}

@Component({
  selector: 'app-faqs',
  imports: [FadeInReveal, RouterLink],
  templateUrl: './faqs.html',
  styleUrl: './faqs.less',
})
export class Faqs implements OnInit {
  faqCategories: FaqCategory[] = [
    {
      title: 'Funcionamiento de las sesiones',
      items: [
        {
          question: '¿Cuánto dura una sesión?',
          answer:
            '<p>Las sesiones tienen una duración aproximada de <strong>45 minutos</strong>.</p>',
          isOpen: false,
        },
        {
          question: '¿Cada cuánto se realizan las sesiones?',
          answer:
            '<p>Normalmente las sesiones se programan <strong>una vez por semana o cada dos semanas</strong>, según el caso y las necesidades del proceso.</p>',
          isOpen: false,
        },
        {
          question: '¿Cuánto dura un proceso terapéutico?',
          answer:
            '<p>La duración depende de cada persona: su historia, el motivo de consulta y el ritmo del propio proceso. Durante las sesiones revisaremos juntas el avance para valorar lo que necesitas.</p>',
          isOpen: false,
        },
      ],
    },
    {
      title: 'Primera visita y Familia',
      items: [
        {
          question: '¿Cómo es la primera sesión?',
          answer: `
            <p>La primera sesión suele estar orientada a conocer la situación, comprender qué preocupa y definir objetivos. El formato varía según a quién acompañemos:</p>
            <ul class="faq-list">
              <li><strong>En adultos:</strong> Hablamos directamente contigo.</li>
              <li><strong>En niños y adolescentes:</strong> Generalmente la primera sesión se realiza <strong>con la familia o cuidadores</strong>. Esto nos sirve para obtener una visión global, conocer la historia emocional del menor y resolver dudas antes de comenzar el trabajo individual.</li>
            </ul>`,
          isOpen: false,
        },
        {
          question: '¿Qué ocurre en las sesiones posteriores con niños?',
          answer:
            '<p>En el caso de terapia infantil, tras la primera visita con la familia, trabajaré directamente con el niño utilizando el <strong>juego terapéutico</strong>. Nos veremos de forma periódica con la familia para dar seguimiento al proceso.</p>',
          isOpen: false,
        },
      ],
    },
    {
      title: 'Privacidad y Comunicación',
      items: [
        {
          question: '¿La terapia es confidencial?',
          answer: `
            <p>Sí. La confidencialidad está garantizada en todos los procesos terapéuticos, adaptándose a la edad:</p>
            <ul class="faq-list">
              <li><strong>Adolescentes:</strong> Se mantiene su privacidad y solo se comparte con la familia información general o aspectos necesarios para su bienestar.</li>
              <li><strong>Niños:</strong> Se informa a la familia del progreso sin revelar contenido emocional específico que pueda poner en riesgo su seguridad interna.</li>
            </ul>`,
          isOpen: false,
        },
        {
          question: '¿Qué ocurre si no sé cómo explicar lo que me pasa?',
          answer:
            '<p>No necesitas tenerlo claro. La terapia también sirve para ordenar lo que sientes, comprender tus emociones y poner palabras a lo que estás viviendo.</p>',
          isOpen: false,
        },
      ],
    },
  ];

  private dom = inject(DOCUMENT);

  ngOnInit() {
    const mainEntity = this.faqCategories.flatMap((category) =>
      category.items.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: this.stripHtml(item.answer),
        },
      })),
    );

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity,
    };

    const script = this.dom.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    this.dom.head.appendChild(script);
  }

  private stripHtml(html: string): string {
    const tmp = this.dom.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  toggleFaq(item: FaqItem): void {
    item.isOpen = !item.isOpen;
  }
}
